import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "flex flex-wrap items-center gap-2 mb-6" }
const _hoisted_2 = {
  key: 0,
  class: "text-muted text-sm"
}
const _hoisted_3 = {
  key: 1,
  class: "flex flex-col gap-8",
  "aria-hidden": "true"
}
const _hoisted_4 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_5 = { class: "text-lg" }
const _hoisted_6 = { class: "text-xs text-muted" }
const _hoisted_7 = {
  key: 2,
  class: "flex flex-col gap-8"
}
const _hoisted_8 = { class: "flex items-center gap-3 mb-2" }
const _hoisted_9 = { class: "text-lg" }
const _hoisted_10 = { class: "text-xs text-muted" }
const _hoisted_11 = { class: "ml-auto flex gap-3 text-sm" }
const _hoisted_12 = ["onClick"]

import type { DevRoeUsesCategory, DevRoeUsesItem } from '#shared/lex'

interface CategoryEntry {
  rkey: string
  uri: string
  cid: string
  value: DevRoeUsesCategory.Record
}

interface ItemEntry {
  rkey: string
  uri: string
  cid: string
  value: DevRoeUsesItem.Record
}


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

definePageMeta({ layout: false })
useHead({ title: 'Uses - admin - Daniel Roe' })

const { data: categoriesData, refresh: refreshCategories, status: categoriesStatus } = useAdminFetch<CategoryEntry[]>('/api/admin/uses-categories', { default: () => [] })
const { data: itemsData, refresh: refreshItems, status: itemsStatus } = useAdminFetch<ItemEntry[]>('/api/admin/uses-items', { default: () => [] })

const showSkeleton = computed(() =>
  (categoriesStatus.value === 'pending' || itemsStatus.value === 'pending')
  && !categoriesData.value.length,
)

const sortedCategories = computed(() => {
  return [...(categoriesData.value ?? [])].sort((a, b) => (a.value.order ?? 100) - (b.value.order ?? 100))
})

/**
 * Per-category reactive item lists wired to formkit/drag-and-drop. Each
 * category's items are sortable independently; cross-category drag is
 * intentionally not enabled (different parent ref).
 */
const categoryDnD = computed(() => {
  return sortedCategories.value.map(category => {
    const itemsForCategory = (itemsData.value ?? [])
      .filter(it => it.value.category?.uri === category.uri)
      .sort((a, b) => (a.value.order ?? 100) - (b.value.order ?? 100))

    return { category, items: itemsForCategory }
  })
})

async function removeCategory (rkey: string, title: string) {
  if (!confirm(`Delete category "${title}"? Items in this category will be left orphaned.`)) return
  const previous = categoriesData.value
  categoriesData.value = categoriesData.value.filter(c => c.rkey !== rkey)
  try {
    await $fetch(`/api/admin/uses-categories/${rkey}`, { method: 'DELETE' })
    refreshCategories()
  }
  catch (error) {
    categoriesData.value = previous
    throw error
  }
}

async function removeItem (rkey: string, name: string) {
  if (!confirm(`Delete item "${name}"?`)) return
  const previous = itemsData.value
  itemsData.value = itemsData.value.filter(i => i.rkey !== rkey)
  try {
    await $fetch(`/api/admin/uses-items/${rkey}`, { method: 'DELETE' })
    refreshItems()
  }
  catch (error) {
    itemsData.value = previous
    throw error
  }
}

/**
 * After a drag-and-drop reorder, persist the new order by writing each
 * item's `order` field to match its array position. Cheap-ish: one PUT per
 * item in the affected category.
 */
async function persistItemOrder (items: ItemEntry[]) {
  const orders = new Map(items.map((item, i) => [item.rkey, (i + 1) * 10]))
  itemsData.value = itemsData.value.map(item => {
    const order = orders.get(item.rkey)
    return order === undefined ? item : { ...item, value: { ...item.value, order } }
  })
  await Promise.all(items.map((item, i) => {
    const value = { ...item.value, order: (i + 1) * 10 }
    delete (value as Record<string, unknown>).$type
    return $fetch(`/api/admin/uses-items/${item.rkey}`, { method: 'PUT', body: value })
  }))
  refreshItems()
}

return (_ctx: any,_cache: any) => {
  const _component_NuxtLink = _resolveComponent("NuxtLink")!
  const _component_AdminSkeleton = _resolveComponent("AdminSkeleton")!
  const _component_AdminSkeletonRows = _resolveComponent("AdminSkeletonRows")!
  const _component_AdminUsesItemDraggable = _resolveComponent("AdminUsesItemDraggable")!
  const _component_AdminShell = _resolveComponent("AdminShell")!

  return (_openBlock(), _createBlock(_component_AdminShell, { title: "Uses" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_component_NuxtLink, {
          to: "/admin/uses/categories/new",
          class: "text-sm bg-accent text-primary px-3 py-1 hover:bg-accent/80 transition-colors"
        }, {
          default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
            _createTextVNode(" New category ", -1)
          ]))]),
          _: 1
        }),
        (_unref(categoriesData)?.length)
          ? (_openBlock(), _createBlock(_component_NuxtLink, {
              key: 0,
              to: "/admin/uses/items/new",
              class: "text-sm bg-primary text-background px-3 py-1 hover:bg-primary/90 transition-colors"
            }, {
              default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                _createTextVNode(" New item ", -1)
              ]))]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      (!_unref(showSkeleton) && !_unref(categoriesData)?.length)
        ? (_openBlock(), _createElementBlock("p", _hoisted_2, " No categories yet. Create a category before adding items. "))
        : _createCommentVNode("", true),
      (_unref(showSkeleton))
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (_openBlock(), _createElementBlock(_Fragment, null, _renderList(3, (i) => {
              return _createElementVNode("section", { key: i }, [
                _createElementVNode("header", _hoisted_4, [
                  _createElementVNode("h2", _hoisted_5, [
                    _createVNode(_component_AdminSkeleton, { class: "bg-accent w-40" })
                  ]),
                  _createElementVNode("span", _hoisted_6, [
                    _createVNode(_component_AdminSkeleton, { class: "bg-accent w-16" })
                  ])
                ]),
                _createVNode(_component_AdminSkeletonRows, {
                  variant: "card",
                  rows: 3
                })
              ])
            }), 64))
          ]))
        : (_openBlock(), _createElementBlock("div", _hoisted_7, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(categoryDnD), (bucket) => {
              return (_openBlock(), _createElementBlock("section", {
                key: bucket.category.rkey
              }, [
                _createElementVNode("header", _hoisted_8, [
                  _createElementVNode("h2", _hoisted_9, _toDisplayString(bucket.category.value.title), 1),
                  _createElementVNode("span", _hoisted_10, "order " + _toDisplayString(bucket.category.value.order ?? 100) + _toDisplayString(bucket.category.value.displayAsGrid ? ' · grid' : ''), 1),
                  _createElementVNode("div", _hoisted_11, [
                    _createVNode(_component_NuxtLink, {
                      to: `/admin/uses/categories/${bucket.category.rkey}`,
                      class: "text-muted hover:text-primary transition-colors"
                    }, {
                      default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                        _createTextVNode(" Edit ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["to"]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "text-muted hover:text-red-500 transition-colors",
                      onClick: ($event: any) => (removeCategory(bucket.category.rkey, bucket.category.value.title))
                    }, " Delete ", 8, _hoisted_12)
                  ])
                ]),
                _createVNode(_component_AdminUsesItemDraggable, {
                  items: bucket.items,
                  onReorder: persistItemOrder,
                  onRemove: removeItem
                }, null, 8, ["items"])
              ]))
            }), 128))
          ]))
    ]),
    _: 1
  }))
}
}

})